import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaSearch, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import PatenteDisplay from './PatenteDisplay';

const AutocompleteSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Buscar...", 
  displayProperty = "nombre",
  valueProperty = "id",
  secondaryProperty = null, // Para mostrar info adicional como stock o precio
  className = "",
  disabled = false,
  renderOption = null, // Función personalizada para renderizar opciones
  isVehicleSelect = false, // Flag para identificar selects de vehículos
  required = false,
  requiredMessage = 'Este campo es requerido'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  // Estado para manejo de validación
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Determinar si mostrar error
  const showError = hasBeenTouched && required && (!value || value === '');

  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        option[displayProperty]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (secondaryProperty && option[secondaryProperty]?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayProperty, secondaryProperty]);

  // Calcular posición del dropdown
  const calculateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // max-h-60 = 240px aproximadamente
      const viewportHeight = window.innerHeight;
      
      // Verificar si hay espacio suficiente abajo
      const spaceBelow = viewportHeight - rect.bottom;
      const shouldShowAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
      
      setDropdownPosition({
        top: shouldShowAbove 
          ? rect.top + window.scrollY - dropdownHeight - 4
          : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Actualizar posición cuando se abre el dropdown
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
      const handleResize = () => calculateDropdownPosition();
      const handleScroll = () => calculateDropdownPosition();
      
      // Encontrar todos los contenedores con scroll
      const scrollableParents = [];
      let element = containerRef.current?.parentElement;
      
      while (element && element !== document.body) {
        const computedStyle = window.getComputedStyle(element);
        if (computedStyle.overflow === 'auto' || 
            computedStyle.overflow === 'scroll' || 
            computedStyle.overflowY === 'auto' || 
            computedStyle.overflowY === 'scroll') {
          scrollableParents.push(element);
        }
        element = element.parentElement;
      }
      
      // Función mejorada que también verifica si el input está visible
      const handleScrollWithVisibilityCheck = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const isVisible = rect.top >= 0 && 
                           rect.left >= 0 && 
                           rect.bottom <= window.innerHeight && 
                           rect.right <= window.innerWidth;
          
          if (isVisible) {
            calculateDropdownPosition();
          } else {
            // Si el input no está visible, cerrar el dropdown
            setIsOpen(false);
          }
        }
      };
      
      // Agregar listeners a window y todos los contenedores scrollables
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScrollWithVisibilityCheck, true);
      
      scrollableParents.forEach(parent => {
        parent.addEventListener('scroll', handleScrollWithVisibilityCheck, true);
      });
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScrollWithVisibilityCheck, true);
        
        scrollableParents.forEach(parent => {
          parent.removeEventListener('scroll', handleScrollWithVisibilityCheck, true);
        });
      };
    }
  }, [isOpen]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener el objeto seleccionado para mostrar el texto
  const selectedOption = options.find(option => option[valueProperty] === value);
  const displayText = selectedOption ? selectedOption[displayProperty] : '';

  const handleOptionSelect = (option) => {
    onChange(option[valueProperty]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  // Manejar cuando pierde el foco (para validación)
  const handleBlur = () => {
    if (!isOpen) {
      setHasBeenTouched(true);
    }
  };

  // Determinar clases CSS según el estado de validación
  const getInputClasses = () => {
    let classes = `w-full bg-gray-700 border rounded-lg px-3 py-2 text-white text-base cursor-pointer flex items-center justify-between transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;
    
    // Si hay error interno o clase de error externa
    const hasErrorBorder = className.includes('error-border');
    if (showError || hasErrorBorder) {
      classes += ' border-red-500 focus-within:border-red-500 focus-within:ring-red-500';
    } else {
      classes += ' border-gray-500 focus-within:border-blue-500';
    }
    
    return classes;
  };

  return (
    <div className={`relative ${className}`} ref={containerRef} data-dropdown-container="true">
      {/* Input principal */}
      <div 
        className={getInputClasses()}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onBlur={handleBlur}
        style={{ fontSize: '16px' }}
      >
        <div className="flex-1 flex items-center">
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent outline-none text-base"
              placeholder={placeholder}
              autoFocus
              disabled={disabled}
              style={{ fontSize: '16px' }}
            />
          ) : (
            <div className={`flex items-center w-full ${displayText ? 'text-white' : 'text-gray-400'}`}>
              {isVehicleSelect && selectedOption ? (
                <div className="flex items-center justify-between w-full">
                  <div>
                    <div className="text-white font-medium">
                      {selectedOption.Marca} {selectedOption.Modelo}
                    </div>
                    <div className="text-xs text-gray-400">
                      {selectedOption.Año || 'N/A'}{selectedOption.Color ? ` - ${selectedOption.Color}` : ''}
                    </div>
                  </div>
                  <PatenteDisplay patente={selectedOption.Patente} size="xs" />
                </div>
              ) : (
                <span>{displayText || placeholder}</span>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          {!isOpen && value && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-white p-1"
              disabled={disabled}
            >
              <FaTimes size={12} />
            </button>
          )}
          {isOpen ? (
            <FaSearch className="text-gray-400" size={12} />
          ) : (
            <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} size={12} />
          )}
        </div>
      </div>

      {/* Dropdown usando Portal */}
      {isOpen && !disabled && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
          data-dropdown="true"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option[valueProperty]}
                className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-base text-white flex justify-between items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelect(option);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ fontSize: '16px' }}
              >
                {renderOption ? (
                  renderOption(option)
                ) : isVehicleSelect ? (
                  // Renderizado especial para vehículos
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium text-white">
                          {option.Marca} {option.Modelo}
                        </div>
                        <div className="text-xs text-gray-400">
                          {option.Año || 'N/A'}{option.Color ? ` - ${option.Color}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <PatenteDisplay patente={option.Patente} size="xs" />
                    </div>
                  </div>
                ) : (
                  // Renderizado por defecto
                  <>
                    <div>
                      <div className="font-medium">{option[displayProperty]}</div>
                      {option.descripcion && (
                        <div className="text-xs text-gray-400">{option.descripcion}</div>
                      )}
                    </div>
                    {secondaryProperty && (
                      <div className="text-xs text-gray-300">
                        {secondaryProperty === 'stock' && `Stock: ${option[secondaryProperty]}`}
                        {secondaryProperty === 'precio' && `$${option[secondaryProperty]?.toLocaleString()}`}
                        {!['stock', 'precio'].includes(secondaryProperty) && option[secondaryProperty]}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-base text-gray-400 text-center" style={{ fontSize: '16px' }}>
              No se encontraron resultados
            </div>
          )}
        </div>,
        document.body
      )}
      
      {/* Mensaje de error personalizado */}
      {showError && (
        <p className="validation-message">
          <FaExclamationTriangle />
          {requiredMessage}
        </p>
      )}
    </div>
  );
};

export default AutocompleteSelect;